import type { IField } from 'shared/entities/form';
import type { ILoadingStageModel } from 'shared/entities/loadingStage';

import type { ProjectSettingsServer } from '../server';

import { BusinessHoursWeekday } from './server';
import type {
  BusinessHoursErrorCode,
  BusinessHoursSettingsServer
} from './server';

export const BUSINESS_HOURS_WEEKDAYS: BusinessHoursWeekday[] = [
  BusinessHoursWeekday.monday,
  BusinessHoursWeekday.tuesday,
  BusinessHoursWeekday.wednesday,
  BusinessHoursWeekday.thursday,
  BusinessHoursWeekday.friday,
  BusinessHoursWeekday.saturday,
  BusinessHoursWeekday.sunday
];

export const BUSINESS_HOURS_AUTO_REPLY_MAX_LENGTH = 1000;

export interface IBusinessHoursDay {
  weekday: BusinessHoursWeekday;
  dayOff: boolean;
  start: string | null;
  end: string | null;
}

export interface IBusinessHoursAutoReply {
  enabled: boolean;
  text: string;
}

export interface IBusinessHoursSettings {
  enabled: boolean;
  schedule: IBusinessHoursDay[];
  autoReply: IBusinessHoursAutoReply;
}

export enum BusinessHoursSaveTarget {
  schedule = 'schedule',
  autoReply = 'autoReply'
}

export type BusinessHoursSerializationOptions = {
  withSavedSchedule?: boolean;
  withSavedAutoReply?: boolean;
};

export interface IBusinessHoursDayModel {
  weekday: BusinessHoursWeekday;
  dayOff: IField<boolean>;
  start: IField<string | null>;
  end: IField<string | null>;
}

export interface IBusinessHoursSettingsModel {
  readonly enabled: IField<boolean>;
  readonly autoReplyEnabled: IField<boolean>;
  readonly autoReplyText: IField<string>;
  readonly days: IBusinessHoursDayModel[];
  readonly touched: boolean;
  readonly isValid: boolean;
  readonly canSave: boolean;
  readonly scheduleTouched: boolean;
  readonly autoReplyCanSave: boolean;

  changeEnabled(value: boolean): void;
  changeAutoReplyEnabled(value: boolean): void;
  changeAutoReplyText(value: string): void;
  changeDayOff(weekday: BusinessHoursWeekday, value: boolean): void;
  changeDayStart(weekday: BusinessHoursWeekday, value: string): void;
  changeDayEnd(weekday: BusinessHoursWeekday, value: string): void;
  validate(): boolean;
  validateSchedule(): boolean;
  reset(): void;
  resetSchedule(): void;
  commitSchedule(): void;
  commitAutoReply(): void;
  toJSON(
    options?: BusinessHoursSerializationOptions
  ): BusinessHoursSettingsServer;
}

export interface IBusinessHoursModel {
  readonly settings: IBusinessHoursSettingsModel | null;
  readonly error: BusinessHoursErrorCode | null;
  readonly loadingStage: ILoadingStageModel;
  readonly savingStage: ILoadingStageModel;
  readonly canSave: boolean;
  readonly isConfigured: boolean;

  changeEnabled(value: boolean): Promise<void>;
  setProjectSettings(data: ProjectSettingsServer): void;
  reload(): Promise<void>;
  save(target?: BusinessHoursSaveTarget): Promise<void>;
  reset(): void;
}
